import { useState, FormEvent } from 'react';

export default function ContactSection() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const form = e.currentTarget;
    const data = new URLSearchParams();
    data.append('first-name', (form.elements.namedItem('first-name') as HTMLInputElement).value);
    data.append('last-name', (form.elements.namedItem('last-name') as HTMLInputElement).value);
    data.append('phone', (form.elements.namedItem('phone') as HTMLInputElement).value);
    data.append('email', (form.elements.namedItem('email') as HTMLInputElement).value);
    data.append('message', (form.elements.namedItem('message') as HTMLTextAreaElement).value);

    try {
      const res = await fetch('https://readdy.ai/api/form/d6vuthfb245v7h34ot2g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-0">
      <div className="grid md:grid-cols-2 min-h-[480px]">
        {/* Left Panel */}
        <div
          className="flex flex-col justify-center px-12 py-16"
          style={{ background: 'linear-gradient(135deg, #001a4d, #002868)' }}
        >
          <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            GET IN TOUCH
          </h2>
          <p className="text-blue-200 text-sm leading-relaxed max-w-xs">
            Feel free to reach out with any questions, feedback, or partnership inquiries. We&apos;re here to help!
          </p>

          <div className="mt-10 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center mt-0.5">
                <i className="ri-map-pin-2-fill text-red-400 text-lg" />
              </div>
              <span className="text-blue-200 text-sm leading-relaxed">1414 Shore District Dr Ste 3101<br />Austin, TX 78741</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-mail-fill text-red-400 text-lg" />
              </div>
              <a href="mailto:gabriel@onpointlabservices.com" className="text-blue-200 text-sm hover:text-white transition-colors">
                gabriel@onpointlabservices.com
              </a>
            </div>
          </div>
        </div>

        {/* Right Form */}
        <div className="bg-white flex flex-col justify-center px-10 py-16">
          {submitted ? (
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-red-100 mx-auto mb-4">
                <i className="ri-check-line text-red-600 text-3xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Message Sent!</h3>
              <p className="text-gray-500 text-sm">Thank you for reaching out. We&apos;ll get back to you shortly.</p>
            </div>
          ) : (
            <form
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">First Name</label>
                  <input
                    name="first-name"
                    type="text"
                    placeholder="E.g. John"
                    className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-red-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Last Name</label>
                  <input
                    name="last-name"
                    type="text"
                    placeholder="E.g. Doe"
                    className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-red-400 transition-colors"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Phone Number</label>
                  <input
                    name="phone"
                    type="tel"
                    placeholder="E.g. +1 3004005000"
                    className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-red-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Email Address <span className="text-red-500">*</span></label>
                  <input
                    name="email"
                    type="email"
                    required
                    placeholder="E.g. john@doe.com"
                    className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-red-400 transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Message</label>
                <textarea
                  name="message"
                  rows={5}
                  maxLength={500}
                  placeholder="Enter your message..."
                  className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-red-400 transition-colors resize-none"
                />
              </div>
              {error && <p className="text-red-500 text-xs">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-red-600 text-white font-semibold rounded-md hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap text-sm disabled:opacity-60"
              >
                {loading ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
